import i18next from "i18next";
import { Constants } from "../models/Constants";
import { getCall, postCall } from "./api";
import { instance } from "./instance";
import { AnalyticalOutputDetailsModel } from "../models/DataAnalysis/DeskReview/AnalyticalOutputDetailsModel";
import { IndicatorReportRequestModel } from "../models/DataAnalysis/DeskReview/IndicatorReportRequestModel";
import {
  IndicatorReportModel,
  TabularReportModel,
} from "../models/DataAnalysis/DeskReview/IndicatorReportDetailsModel";
import { IndicatorsRequestModel } from "../models/RequestModels/AnalyticalOutputRequestModel";
import { notificationService } from "./notificationService";
import { BaseMessageModel } from "../models/ErrorModel";
import { StatusCode } from "../models/Enums";

export const analyticalOutputService = {
  /** Get collection of strategies, indicators, sub-objective, objective information that are associated with the analytical output and assessment
   ** @param assessmentId A string value
   */
  getStrategiesObjectivesSubobjectivesIndicators: (assessmentId: string) =>
    new Promise<AnalyticalOutputDetailsModel>((resolve, reject) => {
      // call an api
      const url =
        Constants.Api.Url.ANALYTICAL_OUTPUT_GET_STRATEGY_OBJECTIVE_SUBOBJECTIVE_INDICATORS.replace(
          "{0}",
          assessmentId
        );

      getCall(url).then((response: any) => {
        if (response) {
          resolve(response);
        } else {
          resolve(AnalyticalOutputDetailsModel.init());
        }
      });
    }),

  /** Get indicators response */
  getIndicatorsResponse: (request: IndicatorReportRequestModel) =>
    new Promise<IndicatorReportModel>((resolve, reject) => {
      postCall<IndicatorReportRequestModel>(
        Constants.Api.Url.ANALYTICAL_OUTPUT_INDICATOR_RESPONSE,
        request
      )
        .then((response: any) => {
          resolve(response);
        })
        .catch(error => reject(error));
    }),

  /** Download report for indicator response */
  downloadReport: async (
    request: IndicatorsRequestModel,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<Blob> => {
    onProgress?.(`${i18next.t("Common.Exporting")}...`);

    const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_REPORT_EXPORT_START}`;
    const jobResponse = await instance.post(startUrl, request, { skipGlobalLoader: true } as any);
    const jobId: string = jobResponse?.data?.jobId;
    if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

    const BACKEND_STEPS = ["", "Fetching data...", "Generating file..."];
    const stepTranslations: Record<string, string> = {
      "Fetching data...":   i18next.t("Common.ExportStepFetchingData") as string,
      "Generating file...": i18next.t("Common.ExportStepGeneratingFile") as string,
    };

    let lastShownIndex = 0;
    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    for (let attempt = 0; attempt < 300; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_REPORT_EXPORT_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
      }
      if (status === "Succeeded") {
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));
        const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_REPORT_EXPORT_DOWNLOAD.replace("{0}", jobId)}`;
        const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
        return downloadResponse.data as Blob;
      }
      if (status === "Failed") {
        notificationService.sendMessage(new BaseMessageModel(i18next.t("Assessment.Message.IndicatorReportExportErrorMessage"), StatusCode.PreConditionFailed));
        throw new Error(errorMessage || i18next.t("Errors.SomethingWentWrong") as string);
      }
    }
    throw new Error("Analytical output report generation timed out.");
  },

  /** Download report for all indicators response */
  downloadReportForAll: async (
    request: IndicatorsRequestModel,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<Blob> => {
    onProgress?.(`${i18next.t("Common.Exporting")}...`);

    const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_REPORT_ALL_EXPORT_START}`;
    const jobResponse = await instance.post(startUrl, request, { skipGlobalLoader: true } as any);
    const jobId: string = jobResponse?.data?.jobId;
    if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

    const BACKEND_STEPS = ["", "Fetching data...", "Generating file..."];
    const stepTranslations: Record<string, string> = {
      "Fetching data...":   i18next.t("Common.ExportStepFetchingData") as string,
      "Generating file...": i18next.t("Common.ExportStepGeneratingFile") as string,
    };

    let lastShownIndex = 0;
    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    for (let attempt = 0; attempt < 300; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_REPORT_ALL_EXPORT_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
      }
      if (status === "Succeeded") {
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));
        const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_REPORT_ALL_EXPORT_DOWNLOAD.replace("{0}", jobId)}`;
        const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
        return downloadResponse.data as Blob;
      }
      if (status === "Failed") {
        notificationService.sendMessage(new BaseMessageModel(i18next.t("Assessment.Message.IndicatorReportExportErrorMessage"), StatusCode.PreConditionFailed));
        throw new Error(errorMessage || i18next.t("Errors.SomethingWentWrong") as string);
      }
    }
    throw new Error("Analytical output report-all generation timed out.");
  },

  /**
   * Get the uploaded diagrams and other information
   * @param assessmentId Assessment Id for which the diagrams will be fetched
   * @param strategyId StrategyId Id for which the diagrams will be fetched
   * @returns List of uploaded diagrams
   */
  getObjectiveUploadedDiagrams: (assessmentId: string, strategyId: string) =>
    new Promise<any>((resolve, reject) => {
      const url =
        Constants.Api.Url.GET_ANALYTICAL_OUTPUT_OBJECTIVE_UPLOAD_DIAGRAM.replace(
          "{0}",
          assessmentId
        ).replace("{1}", strategyId);

      getCall(url).then((response: any) => {
        if (response) {
          resolve(response);
        } else {
          resolve({});
        }
      });
    }),

  /**
   * Download the uploaded diagrams for objective 3 (async polling)
   * @param assessmentId Assessment Id for which the diagrams will be fetched
   * @param strategyId StrategyId Id for which the diagrams will be fetched
   */
  downloadObjectiveDiagrams: async (
    assessmentId: string,
    strategyId: string,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<Blob> => {
    onProgress?.(`${i18next.t("Common.Exporting")}...`);

    const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_OBJ_DIAGRAM_ZIP_START.replace("{0}", assessmentId).replace("{1}", strategyId)}`;
    const jobResponse = await instance.post(startUrl, null, { skipGlobalLoader: true } as any);
    const jobId: string = jobResponse?.data?.jobId;
    if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

    const BACKEND_STEPS = ["", "Fetching diagram files...", "Creating ZIP archive..."];
    const stepTranslations: Record<string, string> = {
      "Fetching diagram files...": i18next.t("Common.ExportStepFetchingDiagrams") as string,
      "Creating ZIP archive...":   i18next.t("Common.ExportStepCreatingZip") as string,
    };

    let lastShownIndex = 0;
    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    for (let attempt = 0; attempt < 300; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_OBJ_DIAGRAM_ZIP_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
      }
      if (status === "Succeeded") {
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));
        const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_OBJ_DIAGRAM_ZIP_DOWNLOAD.replace("{0}", jobId)}`;
        const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
        return downloadResponse.data as Blob;
      }
      if (status === "Failed") {
        notificationService.sendMessage(new BaseMessageModel(i18next.t("Errors.SomethingWentWrong"), StatusCode.PreConditionFailed));
        throw new Error(errorMessage || i18next.t("Errors.SomethingWentWrong") as string);
      }
    }
    throw new Error("Objective diagrams ZIP timed out.");
  },

  /**
   * Download the uploaded diagrams for sub-objective 2.2 (async polling)
   * @param assessmentId Assessment Id for which the diagrams will be fetched
   * @param strategyId StrategyId Id for which the diagrams will be fetched
   */
  downloadSubObjectiveDiagrams: async (
    assessmentId: string,
    strategyId: string,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<Blob> => {
    onProgress?.(`${i18next.t("Common.Exporting")}...`);

    const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_SUBOBJECTIVE_DIAGRAM_ZIP_START.replace("{0}", assessmentId).replace("{1}", strategyId)}`;
    const jobResponse = await instance.post(startUrl, null, { skipGlobalLoader: true } as any);
    const jobId: string = jobResponse?.data?.jobId;
    if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

    const BACKEND_STEPS = ["", "Fetching diagram files...", "Creating ZIP archive..."];
    const stepTranslations: Record<string, string> = {
      "Fetching diagram files...": i18next.t("Common.ExportStepFetchingDiagrams") as string,
      "Creating ZIP archive...":   i18next.t("Common.ExportStepCreatingZip") as string,
    };

    let lastShownIndex = 0;
    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    for (let attempt = 0; attempt < 300; attempt++) {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_SUBOBJECTIVE_DIAGRAM_ZIP_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
      }
      if (status === "Succeeded") {
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));
        const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.ANALYTICAL_OUTPUT_SUBOBJECTIVE_DIAGRAM_ZIP_DOWNLOAD.replace("{0}", jobId)}`;
        const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
        return downloadResponse.data as Blob;
      }
      if (status === "Failed") {
        notificationService.sendMessage(new BaseMessageModel(i18next.t("Errors.SomethingWentWrong"), StatusCode.PreConditionFailed));
        throw new Error(errorMessage || i18next.t("Errors.SomethingWentWrong") as string);
      }
    }
    throw new Error("Sub-objective diagrams ZIP timed out.");
  },
};
